<!-- 组织树侧栏。最后更新：2026-08-26 -->
<template>
	<div class="department-pane" :class="{ 'is-pane-collapsed': isTreeCollapsed }">
		<!-- 折叠时隐去内部数据展示以释放布局 -->
		<div v-show="!isTreeCollapsed" class="pane-content-wrapper">
			<div class="pane-head">
				<div class="pane-head-main">
					<i class="el-icon-s-operation"></i>
					<span>{{ paneTitle }}</span>
				</div>
				<!-- 统一收起入口：标题栏右上角。创建于 2026-08-26 -->
				<el-tooltip
					v-if="!isSchoolScope"
					content="收起部门树(增大右侧空间)"
					placement="top"
					effect="dark">
					<button type="button" class="pane-collapse-btn" @click.stop="toggleCollapse">
						<i class="el-icon-s-fold"></i>
					</button>
				</el-tooltip>
			</div>

			<!-- 可配置的工具条：传入 buttonList / searchList，点击将透传 callFunction 事件 -->
			<toolbar v-if="(buttonList && buttonList.length) || (searchList && searchList.length)" :buttonList="buttonList"
				:searchList="searchList" @callFunction="onToolbarCall"></toolbar>

			<div class="tree-search-box">
				<el-input
					placeholder="输入关键字搜索机构/学校..."
					v-model="filterText"
					clearable
					size="small"
					prefix-icon="el-icon-search"
					class="tree-search-input"
				/>
			</div>

			<el-tree class="flow-tree" @node-click="nodeClick" :data="menus" :props="defaultProps"
				node-key="value" ref="menuTree" :expand-on-click-node="false" :highlight-current="true"
				:default-expanded-keys="expandDefault" :filter-node-method="filterNode">
				<span class="tree-node" slot-scope="{ node, data }">
					<span class="tree-node__dot" :class="'tone-' + nodeTone(data)"></span>
					<span class="tree-node__label" :title="node.label">{{ node.label }}</span>
					<i class="el-icon-arrow-right tree-node__arrow"></i>
				</span>
			</el-tree>
		</div>

		<!-- 收起后：左侧竖向贴边标签，不占用工具栏行。最后更新：2026-08-26 -->
		<el-tooltip
			v-if="!isSchoolScope && isTreeCollapsed"
			content="展开部门树"
			placement="right"
			effect="dark">
			<button
				type="button"
				class="pane-expand-tab"
				@click.stop="toggleCollapse">
				<i class="el-icon-d-arrow-right"></i>
				<span>展开</span>
			</button>
		</el-tooltip>
	</div>
</template>
<script>
import { getDepartmentTree } from '../api/modules/department';
import { resetParentListPage } from '../utils/resetListPage'
import {
	coalesceDirectoryTreeRoot,
	normalizeInternalDirectoryTree,
	resolveActiveSchoolId,
} from '../utils/deptCascaderProps'
import Toolbar from './Toolbar'
export default {
	name: 'DepartmentPane',
	components: { Toolbar },
	data() {
		return {
			isTreeCollapsed: this.directoryScope === 'school' || this.type === 1, // 学校范围直接折叠/隐藏
			originalTree: null, // API 返回的完整未过滤组织树
			menus: [],
			expanded: [],
			lastValidKey: null, // 记录上一次有效(非总平台)节点的 value，便于点击总平台时回退高亮
			pendingSelectionKey: this.initialSelectionKey, // 刷新树后需要恢复的业务节点
			defaultProps: {
				children: 'children',
				label: 'label',
			},
			expandDefault: [], //默认选中第一项
			filterText: '', // 过滤输入字段
		}
	},
	props: {
		/**
		 * 通讯录范围：teaching 教学、internal 内部、school 纯学校、switchable 兼容调用方自行传 type/bigType。
		 * 新页面必须显式传入；旧页面未传时继续沿用 type/bigType，避免全站迁移期间行为突变。
		 */
		directoryScope: {
			type: String,
			default: '',
			validator: value => ['', 'teaching', 'internal', 'school', 'switchable'].indexOf(value) !== -1,
		},
		// 侧栏标题，与异动/毕业页 filter-head 风格对齐
		paneTitle: {
			type: String,
			default: () => '组织架构',
		},
		// 是否阻止“总平台/全部(通常 value=0)”被选中，从而要求用户先选择具体学校再进行查询/新增/导出等操作
		blockTotalPlatform: {
			type: Boolean,
			default: () => true,
		},
		/**
		 * 是否在树顶插入「平台账号（未分配部门）」虚拟节点（value=-1，接口传 departmentId=0）。
		 * 用于用户管理等需要查看未分配部门总账号的场景。最后修改：2026-08-27
		 */
		showUnassignedNode: {
			type: Boolean,
			default: () => false,
		},
		/** 虚拟节点展示文案 */
		unassignedNodeLabel: {
			type: String,
			default: () => '平台账号（未分配部门）',
		},
		/**
		 * 是否在树顶插入「全部学校账号」虚拟节点（value=-2）。
		 * 超管始终展示，不依赖顶栏学校。最后修改：2026-08-27
		 */
		showAllSchoolsNode: {
			type: Boolean,
			default: () => false,
		},
		allSchoolsNodeLabel: {
			type: String,
			default: () => '全部学校账号',
		},
		// 教学架构工作台首次进入时选中学校根节点，右侧展示直属组织列表。
		defaultSelectRoot: {
			type: Boolean,
			default: () => false,
		},
		// 页面进入时需要恢复的组织节点 Id，优先于默认根节点。最后更新：2026-10-20
		initialSelectionKey: {
			type: [String, Number],
			default: null,
		},
		totalPlatformMessage: {
			type: String,
			default: () => '当前处于总平台状态，请先从左侧选择具体的机构/学校再进行操作',
		},
		type: {
			type: Number,
			default: () => {
				return 0
			},
		},
		bigType: {
			//0：内部通讯录、1：家校通讯录
			bigType: Number,
			default: () => {
				return 0
			},
		},
		// 可选：页面传入工具栏配置
		buttonList: { type: Array, default: () => [] },
		searchList: { type: Array, default: () => [] },
	},
	computed: {
		scopeConfig() {
			const configMap = {
				teaching: { type: 0, bigType: 1, schoolOnly: false },
				internal: { type: 0, bigType: 0, schoolOnly: false },
				school: { type: 1, bigType: 0, schoolOnly: true },
				switchable: { type: this.type, bigType: this.bigType, schoolOnly: this.type === 1 },
			}
			return configMap[this.directoryScope] || {
				type: this.type,
				bigType: this.bigType,
				schoolOnly: this.type === 1,
			}
		},
		isSchoolScope() {
			return this.scopeConfig.schoolOnly
		},
		requiresSchoolScope() {
			return ['teaching', 'internal', 'school'].indexOf(this.directoryScope) !== -1
		},
		/**
		 * 超管用户管理展示全平台树，不跟随顶栏学校。最后更新：2026-09-03
		 */
		isPlatformWideTree() {
			return !!this.showAllSchoolsNode
		},
	},
	watch: {
		filterText(val) {
			this.$refs.menuTree.filter(val);
		},
		// 监听全局 Vuex 活跃学校 ID 的变化，自动过滤裁剪部门树
		'$store.state.activeSchoolId': {
			handler(newSchoolId) {
				if (this.isPlatformWideTree) return
				if (this.isSchoolScope) {
					this.applySchoolFilter(newSchoolId)
					return
				}
				this.handleQuery()
			},
			immediate: true
		},
		showAllSchoolsNode(val) {
			if (val) {
				this.handleQuery()
				return
			}
			if (this.originalTree) {
				this.applySchoolFilter(this.$store.state.activeSchoolId)
			}
		},
	},
	methods: {
		// 树节点搜索过滤条件判断
		filterNode(value, data) {
			if (!value) return true;
			return data.label && data.label.indexOf(value) !== -1;
		},
		// 节点色点：按组织类型区分，对齐异动侧栏 tone 体系
		nodeTone(data) {
			const type = String((data && (data.DeptOrgType || data.deptOrgType)) || '').toLowerCase()
			if (type === 'platform') return 'platform'
			if (type === 'allschools') return 'school'
			if (type === 'school' || type === 'affiliateschool') return 'school'
			if (type === 'campus') return 'campus'
			if (type === 'period' || type === 'stage') return 'period'
			if (type === 'grade') return 'grade'
			if (type === 'class') return 'class'
			return 'default'
		},
		isTotalPlatformValue(value) {
			// value 可能为 0 / '0' / null / undefined
			const n = Number(value);
			return !n || n <= 0;
		},
		/**
		 * 是否为「平台账号」虚拟节点（允许选中，不受 blockTotalPlatform 拦截）。创建于 2026-08-26
		 */
		isUnassignedPlatformNode(data) {
			if (!this.showUnassignedNode || !data) return false;
			const orgType = String(data.DeptOrgType || data.deptOrgType || '').toLowerCase();
			// 虚拟节点用 -1，避免与「总平台」根节点 value=0 冲突
			return Number(data.value) === -1 && orgType === 'platform';
		},
		/**
		 * 是否为「全部学校账号」虚拟节点。创建于 2026-08-27
		 */
		isAllSchoolsNode(data) {
			if (!this.showAllSchoolsNode || !data) return false;
			const orgType = String((data.DeptOrgType || data.deptOrgType) || '').toLowerCase();
			return Number(data.value) === -2 && orgType === 'allschools';
		},
		/**
		 * 在树顶插入虚拟节点。最后修改：2026-08-27
		 */
		prependUnassignedNode(menus) {
			const list = Array.isArray(menus) ? menus.slice() : [];
			if (this.showUnassignedNode) {
				const exists = list.some((n) => this.isUnassignedPlatformNode(n));
				if (!exists) {
					list.unshift({
						value: -1,
						label: this.unassignedNodeLabel,
						DeptOrgType: 'platform',
						deptOrgType: 'platform',
						children: [],
					});
				}
			}
			if (this.showAllSchoolsNode) {
				const exists = list.some((n) => this.isAllSchoolsNode(n));
				if (!exists) {
					list.unshift({
						value: -2,
						label: this.allSchoolsNodeLabel,
						DeptOrgType: 'allschools',
						deptOrgType: 'allschools',
						children: [],
					});
				}
			}
			return list;
		},
		tryEmitTreeNodeClick(data, node, component) {
			// 如果点击了未被直接授权的引导性祖先节点，直接拦截并回退高亮
			if (data && data.disabled) {
				if (this.lastValidKey !== null && this.$refs.menuTree && this.$refs.menuTree.setCurrentKey) {
					this.$nextTick(() => {
						try {
							this.$refs.menuTree.setCurrentKey(this.lastValidKey);
						} catch (e) {
							// ignore
						}
					});
				}
				return;
			}

			// 普通「总平台」根仍可拦截；「平台账号」虚拟节点允许选中
			if (this.blockTotalPlatform && this.isTotalPlatformValue(data && data.value)
				&& !this.isUnassignedPlatformNode(data) && !this.isAllSchoolsNode(data)) {
				if (this.$message) {
					this.$message({ message: this.totalPlatformMessage, type: 'error' });
				}
				// 回退到上一次有效节点，避免 UI 高亮切到“总平台”
				if (this.lastValidKey !== null && this.$refs.menuTree && this.$refs.menuTree.setCurrentKey) {
					// nextTick 确保 el-tree 已完成当前点击的内部状态更新
					this.$nextTick(() => {
						try {
							this.$refs.menuTree.setCurrentKey(this.lastValidKey);
						} catch (e) {
							// ignore
						}
					});
				}
				return;
			}

			// 仅当节点有效时记录
			if (data && data.value !== undefined) {
				this.lastValidKey = data.value;
			}
			// 切换机构/学校/年级/班级时重置列表页码，避免仍停留在第 N 页。创建于 2026-08-02
			resetParentListPage(this)
			this.$emit('tree-node-click', data, node, component);
		},
		nodeClick(data, node, component) {
			this.tryEmitTreeNodeClick(data, node, component)
		},
		onToolbarCall(item) {
			// 直接向上透传，以便父组件复用统一的 callFunction 处理
			this.$emit('callFunction', item)
		},
		refreshMethod(preferredKey) {
			this.pendingSelectionKey = preferredKey === undefined || preferredKey === null
				? null
				: preferredKey
			this.handleQuery()
		},
		isValidSchoolId(schoolId) {
			const value = String(schoolId == null ? '' : schoolId)
			return /^\d+$/.test(value) && value !== '0'
		},
		/** 读取顶栏当前学校 ID（store 优先，localStorage 兜底）。雪花 ID 保持字符串。最后更新：2026-10-15 */
		getActiveSchoolId() {
			return resolveActiveSchoolId(this) || 0
		},
		/**
		 * 同一学校的过期响应不能把后一次结果盖掉。最后更新：2026-10-15
		 * @param {boolean} [bypassCache]
		 */
		handleQuery(bypassCache) {
			const seq = (this._treeReqSeq || 0) + 1
			this._treeReqSeq = seq
			const platformWide = this.isPlatformWideTree
			const activeSchoolId = platformWide ? 0 : this.getActiveSchoolId()
			if (!platformWide && this.requiresSchoolScope && !this.isValidSchoolId(activeSchoolId)) {
				this.originalTree = null
				this.menus = []
				this.lastValidKey = null
				this._treeSchoolKey = ''
				return
			}
			if (this.isSchoolScope) {
				// 学校级别树，全部由全局顶栏 Header 接管，这里不需要拉取后端树
				this.applySchoolFilter(activeSchoolId)
				return
			}
			const schoolKey = platformWide ? 'platform' : String(activeSchoolId)
			// 同一学校刷新时先留着旧树，避免请求被丢掉后停在「暂无数据」
			if (this._treeSchoolKey !== schoolKey) {
				this.originalTree = null
				this.menus = []
				this.lastValidKey = null
				this._treeSchoolKey = schoolKey
				this._treeRetry = 0
			}
			// scopeConfig 已写好 type/bigType。直接把对象传给取树接口。
			// 线上 webpack 曾把 getDepartmentTreeByScope 绑到 getDepartmentTree，字符串 "teaching" 被当成参数，bigType 全部变成 0。最后更新：2026-09-23
			let para = {
				pid: 0,
				type: this.scopeConfig.type,
				bigType: this.scopeConfig.bigType,
				scopeDepartmentId: platformWide ? 0 : activeSchoolId
			}
			getDepartmentTree(para, bypassCache ? { bypassCache: true } : undefined).then((res) => {
				if (seq !== this._treeReqSeq) return
				if (!platformWide && this.requiresSchoolScope
					&& String(activeSchoolId) !== String(this.getActiveSchoolId())) return
				const body = res && res.data
				if (body && body.response) {
					let tree = body.response
					// 内部通讯录前端兜底：剔除校区等家校节点，子行政部门抬升保留。最后更新：2026-08-30
					if (this.directoryScope === 'internal') {
						tree = coalesceDirectoryTreeRoot(normalizeInternalDirectoryTree(tree))
					}
					this.originalTree = tree
					this._treeRetry = 0
					this.applySchoolFilter(platformWide ? 0 : this.getActiveSchoolId())
					return
				}
				this.retryDepartmentTree(seq)
			}).catch(() => {
				if (seq !== this._treeReqSeq) return
				this.retryDepartmentTree(seq)
			})
		},
		/** 树接口偶发失败时绕过缓存再取一次。最后更新：2026-10-15 */
		retryDepartmentTree(seq) {
			if (this._treeRetry) return
			this._treeRetry = 1
			this.$nextTick(() => {
				if (seq !== this._treeReqSeq) return
				this.handleQuery(true)
			})
		},
		/**
		 * 全平台树去掉总平台壳，直接展示各学校。创建于 2026-09-03
		 */
		resolvePlatformWideRoots(root) {
			if (!root) return []
			const value = Number(root.value)
			const label = String(root.label || root.Name || '')
			const orgType = String(root.DeptOrgType || root.deptOrgType || '').toLowerCase()
			if (value === 0 || label === '总平台' || orgType === 'platform') {
				return Array.isArray(root.children) ? root.children.slice() : []
			}
			return [root]
		},
		// 根据学校 ID 过滤部门树，仅展示选定学校的内部树
		applySchoolFilter(schoolId) {
			if (this.isSchoolScope) {
				if (!this.isValidSchoolId(schoolId)) {
					this.menus = []
					return
				}
				// 顶栏切换学校等同组织切换，先重置页码再触发业务加载。创建于 2026-08-02
				resetParentListPage(this)
				// 直接向父页面发射点击事件，传递虚拟的学校节点数据，模拟点击事件以触发父页面的数据加载
				this.$emit('tree-node-click', {
					value: schoolId,
					label: this.$store.state.activeSchoolName || '',
					DeptOrgType: 'school'
				})
				return
			}
			if (!this.originalTree) return
			if (this.isPlatformWideTree) {
				const roots = this.resolvePlatformWideRoots(this.originalTree)
				this.menus = this.prependUnassignedNode(roots)
				this.setDefaultSelection(this.originalTree)
				return
			}
			if (this.requiresSchoolScope && !this.isValidSchoolId(schoolId)) {
				this.menus = []
				this.lastValidKey = null
				return
			}
			if (!schoolId) {
				this.menus = this.prependUnassignedNode([this.originalTree])
				this.setDefaultSelection(this.originalTree)
				return
			}
			const targetNode = this.findSchoolNode(this.originalTree, schoolId)
			if (targetNode) {
				this.menus = this.prependUnassignedNode([targetNode])
				this.setDefaultSelection(targetNode)
			} else {
				this.menus = this.prependUnassignedNode([this.originalTree])
				this.setDefaultSelection(this.originalTree)
			}
		},
		// 递归寻找以 schoolId 为 value 且组织类型是 School/AffiliateSchool 的节点
		findSchoolNode(node, schoolId) {
			if (!node) return null
			const orgType = String(node.DeptOrgType || node.deptOrgType || '').toLowerCase()
			const val = String(node.value)
			if (val === String(schoolId) && (orgType === 'school' || orgType === 'affiliateschool')) {
				return node
			}
			if (node.children && node.children.length) {
				for (let i = 0; i < node.children.length; i++) {
					const found = this.findSchoolNode(node.children[i], schoolId)
					if (found) return found
				}
			}
			return null
		},
		// 默认展开并高亮选中：超管优先「全部学校账号」。最后修改：2026-08-27
		setDefaultSelection(rootNode) {
			const allSchoolsNode = (this.menus || []).find((n) => this.isAllSchoolsNode(n))
			if (allSchoolsNode && this.pendingSelectionKey === null) {
				this.expandDefault = []
				this.pendingSelectionKey = null
				// 立即通知父页，避免右侧空态闪烁。最后更新：2026-08-30
				this.tryEmitTreeNodeClick(allSchoolsNode, { data: allSchoolsNode, parent: null }, {})
				this.$nextTick(() => {
					const tree = this.$refs.menuTree
					if (!tree) return
					try {
						if (tree.setCurrentKey) tree.setCurrentKey(allSchoolsNode.value)
					} catch (e) {
						// ignore
					}
				})
				return
			}
			const firstBranch = this.deepLastNode(rootNode)
			const preferredPath = this.pendingSelectionKey === null
				? []
				: this.findNodePath(rootNode, this.pendingSelectionKey)
			const selectedNodes = preferredPath.length
				? [preferredPath[preferredPath.length - 1]]
				: (this.defaultSelectRoot ? [rootNode] : firstBranch)
			this.expandDefault = preferredPath.length
				? preferredPath.slice(0, -1).map(item => item.value)
				: firstBranch.map(item => item.Pid)
			this.pendingSelectionKey = null
			if (!selectedNodes.length) return
			const leaf = selectedNodes[selectedNodes.length - 1]
			if (leaf == null || leaf.value === undefined) return

			// defaultSelectRoot：立刻抛出学校节点，去掉 200ms 空窗。最后更新：2026-08-30
			if (this.defaultSelectRoot || preferredPath.length) {
				const pathNodes = preferredPath.length
					? preferredPath
					: [rootNode]
				const fakeNode = this.buildTreeNodeChain(pathNodes)
				this.tryEmitTreeNodeClick(leaf, fakeNode || { data: leaf, parent: null }, {})
				this.$nextTick(() => {
					const tree = this.$refs.menuTree
					if (!tree) return
					try {
						if (tree.setCurrentKey) tree.setCurrentKey(leaf.value)
					} catch (e) {
						// ignore
					}
				})
				return
			}

			this.$nextTick(() => {
				setTimeout(() => {
					const tree = this.$refs.menuTree
					if (!tree) return
					try {
						if (tree.setCurrentKey) tree.setCurrentKey(leaf.value)
					} catch (e) {
						// ignore
					}
					const elNode = tree.getNode ? tree.getNode(leaf.value) : null
					this.tryEmitTreeNodeClick(leaf, elNode || { data: leaf, parent: null }, {})
				}, 200)
			})
		},
		/** 用数据路径拼出近似 el-tree node，便于父页向上解析学校。最后更新：2026-08-30 */
		buildTreeNodeChain(pathNodes) {
			if (!pathNodes || !pathNodes.length) return null
			let parent = null
			let current = null
			pathNodes.forEach((item) => {
				current = { data: item, parent }
				parent = current
			})
			return current
		},
		findNodePath(node, value, path = []) {
			if (!node) return []
			const currentPath = path.concat([node])
			if (String(node.value) === String(value)) return currentPath
			const children = node.children || []
			for (let index = 0; index < children.length; index += 1) {
				const found = this.findNodePath(children[index], value, currentPath)
				if (found.length) return found
			}
			return []
		},
		deepLastNode(obj) {
			if (obj.children && obj.children.length) {
				let temp = obj.children[0]
				return [...this.deepLastNode(temp)]
			} else {
				return [obj]
			}
		},
		toggleCollapse() {
			this.isTreeCollapsed = !this.isTreeCollapsed
			if (this.$el && this.$el.parentNode) {
				const parent = this.$el.parentNode
				if (this.isTreeCollapsed) {
					parent.classList.add('is-collapsed')
				} else {
					parent.classList.remove('is-collapsed')
				}
			}
			this.$emit('collapse-change', this.isTreeCollapsed)
		}
	},
	beforeDestroy() {
		if (this.$el && this.$el.parentNode) {
			this.$el.parentNode.classList.remove('is-collapsed')
		}
	},
	mounted() {
		if (this.isSchoolScope) {
			if (this.$el && this.$el.parentNode) {
				this.$el.parentNode.classList.add('is-collapsed')
			}
			// activeSchoolId 的 immediate 监听已完成学校级页面的首次加载。
			return
		}
		// 教学树等：watch immediate 已 handleQuery；全平台树 watch 会直接 return，需在此补一次
		if (this.isPlatformWideTree) this.handleQuery()
	},
}
</script>

<style lang="stylus" scoped>
/* 与 button-theme / toolbar-theme 同套。最后修改：2026-08-29 */
.department-pane {
  --pane-primary: var(--btn-primary, #2563eb);
  --pane-ink: #1e293b;
  --pane-muted: var(--btn-neutral, #64748b);
  --pane-line: #e8edf3;
  --pane-soft: var(--btn-primary-plain-bg, #eff6ff);
  --pane-soft-border: var(--btn-primary-plain-border, #bfdbfe);
  --pane-hover: #f1f5f9;
  /* visible：折叠小耳朵可伸出；横向裁切交给 pane-content-wrapper / 树 */
  overflow: visible !important;
  border: 0;
  border-radius: 0;
  background: transparent;
  height: 100%;
  width: 100%;
  min-width: 0;
  box-shadow: none;
  display: flex;
  flex-direction: column;
}

.pane-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 600;
  color: var(--pane-ink);
  border-bottom: 1px solid var(--pane-line);
  flex-shrink: 0;
}

.pane-head-main {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.pane-head-main span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pane-head-main i {
  color: var(--pane-primary);
  flex-shrink: 0;
}

/* 仿 el-button--primary.is-plain */
.pane-collapse-btn {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border: 1px solid var(--pane-soft-border);
  border-radius: var(--btn-radius, 8px);
  background: var(--pane-soft);
  color: var(--pane-primary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease;
  padding: 0;
}

.pane-collapse-btn:hover {
  background: #dbeafe;
  border-color: var(--btn-primary-hover, #1d4ed8);
  color: var(--btn-primary-hover, #1d4ed8);
}

.pane-collapse-btn i {
  font-size: 14px;
  color: inherit;
}

/* 收起后：竖向贴边标签，避免横条挤进工具栏 */
.pane-expand-tab {
  position: absolute;
  top: 50%;
  right: -30px;
  left: auto;
  transform: translateY(-50%);
  z-index: 120;
  width: 30px;
  min-height: 96px;
  padding: 14px 0;
  border: 1px solid var(--pane-soft-border);
  border-left: none;
  border-radius: 0 10px 10px 0;
  background: #ffffff;
  color: var(--pane-primary);
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  box-shadow: 2px 2px 8px rgba(37, 99, 235, 0.1);
  transition: background-color 0.18s ease, border-color 0.18s ease, width 0.18s ease, right 0.18s ease;
}

.pane-expand-tab:hover {
  width: 34px;
  right: -34px;
  background: var(--pane-soft);
  border-color: var(--pane-soft-border);
}

.pane-expand-tab i {
  font-size: 14px;
  line-height: 1;
}

.pane-expand-tab span {
  writing-mode: vertical-rl;
  letter-spacing: 3px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
}

.tree-search-box {
  padding: 8px 12px 6px;
  flex-shrink: 0;
}

.tree-search-input >>> .el-input__inner {
  border-radius: var(--btn-radius, 8px);
  background-color: #f8fafc;
  border: 1px solid var(--pane-line);
  color: var(--pane-ink);
  transition: border-color 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease;
}

.tree-search-input >>> .el-input__inner:focus {
  border-color: var(--pane-primary);
  background-color: #ffffff;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.department-pane > .toolbar,
.pane-content-wrapper > .toolbar {
  margin-bottom: 0;
  padding: 8px 12px 0;
}

.department-pane >>> .el-tree {
  background: transparent;
  padding: 4px 0 12px;
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  width: 100%;
  /* 只纵向滑：展开层级用省略号，避免横向滚动条把节点「滑没」。最后修改：2026-08-24 */
  overflow-x: hidden !important;
  overflow-y: auto !important;
  /* 覆盖全站 14px 滚动条，部门树用细条。最后修改：2026-08-27 */
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.department-pane >>> .el-tree::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.department-pane >>> .el-tree::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 6px;
}

.department-pane >>> .el-tree::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 6px;
  border: none;
  background-clip: padding-box;
}

.department-pane >>> .el-tree::-webkit-scrollbar-thumb:hover {
  background-color: #94a3b8;
}

.department-pane >>> .el-tree-node {
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
}

.department-pane >>> .el-tree-node__content {
  height: 38px;
  margin: 2px 8px;
  padding-right: 8px;
  border-radius: var(--btn-radius, 8px);
  transition: background-color 0.18s ease, color 0.18s ease;
  color: var(--pane-ink);
  overflow: hidden;
  box-sizing: border-box;
  width: auto;
  max-width: calc(100% - 16px);
}

.department-pane >>> .el-tree-node__content > .el-tree-node__label,
.department-pane >>> .el-tree-node__content > span {
  min-width: 0;
  overflow: hidden;
}

.department-pane >>> .el-tree-node__content:hover {
  background-color: var(--pane-hover);
}

.department-pane >>> .el-tree-node.is-current > .el-tree-node__content {
  background-color: var(--pane-soft);
  color: var(--pane-primary);
  font-weight: 500;
}

.department-pane >>> .el-tree-node.is-current > .el-tree-node__content .el-tree-node__expand-icon {
  color: var(--pane-primary);
}

.department-pane >>> .el-tree-node__expand-icon {
  color: #94a3b8;
  font-size: 12px;
  padding: 4px;
  flex-shrink: 0;
}

/* 隐藏叶子展开图标，保留占位对齐缩进 */
.department-pane >>> .el-tree-node__expand-icon.is-leaf {
  visibility: hidden;
}

.tree-node {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
  max-width: 100%;
  overflow: hidden;
  padding-right: 2px;
}

.tree-node__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: #94a3b8;
}

.tree-node__dot.tone-school { background: var(--btn-primary, #2563eb); }
.tree-node__dot.tone-campus { background: #f59e0b; }
.tree-node__dot.tone-period { background: #8b5cf6; }
.tree-node__dot.tone-grade { background: #06b6d4; }
.tree-node__dot.tone-class { background: #64748b; }
.tree-node__dot.tone-platform { background: #f59e0b; }
.tree-node__dot.tone-default { background: #94a3b8; }

.tree-node__label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  line-height: 1.4;
}

.tree-node__arrow {
  font-size: 12px;
  color: #cbd5e1;
  opacity: 0;
  transition: opacity 0.18s ease;
  flex-shrink: 0;
}

.department-pane >>> .el-tree-node__content:hover .tree-node__arrow,
.department-pane >>> .el-tree-node.is-current > .el-tree-node__content .tree-node__arrow {
  opacity: 1;
}

.flow-tree {
  display: block;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

/* 内层不单独做卡片，避免与 dp-left 双层圆角。最后修改：2026-08-29 */
.pane-content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  background: transparent;
  border-radius: 0;
}

@media screen and (max-width: 768px) {
  .department-pane {
    max-height: 100%;
  }

  .pane-head {
    padding: 8px 12px;
    font-size: 14px;
  }

  .tree-search-box {
    padding: 8px 10px 6px;
  }
}
</style>
